import { useMutation, useQuery } from '@apollo/client'
import classNames from 'classnames'
import moment from 'moment'
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { Loader, NotificationType } from '../../components'
import {
  ChatInput,
  ChatMembersList,
  ChatMessage,
  CollaborationTips,
} from '../../components/Chat'
import { GET_BRAND_TEMPLATES, QueryGetBrandTemplatesResponse } from '../../gql'
import {
  GET_MESSAGES_BY_THREAD,
  GET_MESSAGES_THREADS,
  GET_MESSAGE_THREAD_BY_UUID,
  GetMessageThreadByUuidResponse,
  GetMessagesByThreadResponse,
  MARK_ALL_MESSAGES_AS_READ_BY_BRAND,
  MARK_THREAD_MESSAGES_AS_READ_BY_BRAND,
} from '../../gql/messages'
import {
  GetMessagesSort,
  GetMessagesThreadsSort,
  GetTemplatesSort,
  Message,
  MessageType,
  QueryGetBrandTemplatesArgs,
  QueryGetMessageThreadByUuidArgs,
  QueryGetMessagesByThreadArgs,
  ThreadStatus,
  BrandServiceLevel,
} from '../../gql/types'
import { PricingData } from '../../utils/PricingUtil'
import { useNotification } from '../../utils/hooks'
import { BrandRoutes } from '../BrandRoutes'
import { Header } from './components/Header'

interface IProps {
  pricing: PricingData
}

const Inbox: FunctionComponent<IProps> = ({ pricing }) => {
  const { addNotification } = useNotification()
  const messagesEndRef = useRef(null)

  const { id, uuid } = useParams()

  const [query, setQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const getFiltersWithArchived = (includeArchived: boolean) => ({
    creatorId: null,
    brandId: parseInt(id),
    status: includeArchived
      ? [ThreadStatus.active, ThreadStatus.archived]
      : [ThreadStatus.active],
  })

  const filtersDefault = getFiltersWithArchived(false)
  const sortDefault = GetMessagesThreadsSort.lastMessageSent

  const [filters, setFilters] = useState(filtersDefault)
  const [sort, setSort] = useState<GetMessagesThreadsSort>(
    (searchParams.get('sort') as GetMessagesThreadsSort) || sortDefault,
  )

  useEffect(() => {
    searchParams.set('sort', sort)
    setSearchParams(searchParams)
  }, [sort])

  const { data: thread } = useQuery<
    GetMessageThreadByUuidResponse,
    QueryGetMessageThreadByUuidArgs
  >(GET_MESSAGE_THREAD_BY_UUID, {
    fetchPolicy: 'cache-and-network',
    variables: {
      uuid,
    },
    skip: !uuid,
  })

  // TODO: Rename 'activeThread' - confusing name! This refers to the currently SELECTED thread in UI,
  // not the thread STATUS (active vs archived). Better names: 'selectedThread' or 'currentThread'
  const activeThread = useMemo(() => {
    if (thread && thread?.getMessageThreadByUuid) {
      return thread.getMessageThreadByUuid
    }

    return null
  }, [thread])

  const [threadsSkip, setThreadsSkip] = useState(0)
  const THREADS_LIMIT = 20

  const {
    loading: threadsLoading,
    error: threadsError,
    data: threads,
    refetch: refetchMessageThreads,
  } = useQuery(GET_MESSAGES_THREADS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      options: {
        query,
        filters: {
          ...filters,
        },
        sort,
        skip: threadsSkip,
        limit: THREADS_LIMIT,
      },
    },
  })

  const fetchMoreThreads = () => {
    if (!threadsLoading) {
      setThreadsSkip(threadsSkip + THREADS_LIMIT)
    }
  }

  const {
    loading: messagesLoading,
    data: messages,
    refetch: refetchMessages,
  } = useQuery<GetMessagesByThreadResponse, QueryGetMessagesByThreadArgs>(
    GET_MESSAGES_BY_THREAD,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        threadId: activeThread?.id,
        options: {
          sort: GetMessagesSort.oldest,
          skip: 0,
          limit: 200,
        },
      },
      skip: !activeThread,
    },
  )

  const [markThreadMessagesAsReadByBrand] = useMutation(
    MARK_THREAD_MESSAGES_AS_READ_BY_BRAND,
  )

  useEffect(() => {
    if (activeThread?.uuid) {
      markThreadMessagesAsReadByBrand({
        variables: {
          uuid: activeThread?.uuid,
        },
      }).catch((error) => console.log(error))
    }
  }, [activeThread?.uuid])

  // Update filters when showArchived changes
  useEffect(() => {
    setFilters(getFiltersWithArchived(showArchived))
  }, [showArchived])

  useEffect(() => {
    setThreadsSkip(0)
  }, [query, filters, sort])

  const [markAllMessagesAsReadByBrand] = useMutation(
    MARK_ALL_MESSAGES_AS_READ_BY_BRAND,
  )

  // get templates by user self
  const { error: getBrandTemplatesError, data: getBrandTemplatesData } =
    useQuery<QueryGetBrandTemplatesResponse, QueryGetBrandTemplatesArgs>(
      GET_BRAND_TEMPLATES,
      {
        variables: {
          brandId: parseInt(id),
          options: {
            query: '',
            filters: {
              // ownerUserId: [user?.id],
            },
            sort: GetTemplatesSort.newest,
            skip: 0,
            limit: 100,
          },
        },
      },
    )

  const onMarkAllRead = async () => {
    try {
      await markAllMessagesAsReadByBrand({
        variables: {
          id: parseInt(id),
        },
      })

      addNotification('All messages marked as read', NotificationType.success)
    } catch (error) {
      addNotification(
        `Something went wrong. Please try again.`,
        NotificationType.error,
      )
    }
  }

  const onSearch = (query: string) => {
    setQuery(query)
  }

  const onToggleArchived = async (show: boolean) => {
    setShowArchived(show)
    setThreadsSkip(0) // Reset pagination when toggling

    // Force refetch with new filter
    const newFilters = getFiltersWithArchived(show)
    await refetchMessageThreads({
      options: {
        query,
        filters: newFilters,
        sort,
        skip: 0,
        limit: THREADS_LIMIT,
      },
    })
  }

  // Get current brand from first available thread (all threads belong to same brand)
  const currentBrand = useMemo(() => {
    return threads?.getMessagesThreads?.items?.[0]?.brand || null
  }, [threads?.getMessagesThreads?.items])

  if (threadsError) {
    return <p className='p-4'>Something went wrong. Please navigate back.</p>
  }

  if (!id) {
    return <Loader />
  }

  const renderMessage = (message: Message) => {
    let name,
      avatar,
      userIsAuthor,
      checkedStatus = message.checkedStatus,
      text = message.message,
      adminMessage = message.adminMessage
    switch (message.type) {
      case MessageType.brandToCreator:
        name = activeThread?.brand?.name
        avatar = activeThread?.brand?.logo
        userIsAuthor = true
        break
      case MessageType.creatorToBrand:
        name = `${activeThread?.creator?.firstName}`
        avatar = activeThread?.creator?.avatar
        userIsAuthor = false
        break
      default:
        name = 'Clip'
        avatar = `https://d1td5d11gfjk4b.cloudfront.net/logo/logo.png`
    }
    const author = {
      name,
      avatar,
      userIsAuthor,
    }
    return (
      <div key={message.uuid}>
        <ChatMessage
          author={author}
          datetime={
            message.createdAt
              ? moment(message.createdAt).format('ll LT')
              : undefined
          }
          message={text}
          checkedStatus={checkedStatus}
          userIsAuthor={userIsAuthor}
          adminMessage={adminMessage}
          isEditRequest={message.isEditClipRequest}
          isSecondEditRequest={message.isSecondEditClipRequest}
          attachmentUrl={message?.file?.url}
          attachmentName={message?.file?.name}
          attachmentThumbnailUrl={message?.thumbnail?.url}
          attachmentMime={message.attachmentMime}
        />
      </div>
    )
  }

  const renderChat = () => {
    if (!activeThread) {
      return null
    }

    return (
      <div className='flex flex-col w-full justify-between relative'>
        <Header
          className='p-4 bg-gray-50'
          creator={activeThread?.creator}
          brand={activeThread?.brand}
          pricing={pricing}
          activeThread={activeThread}
          isDefaultTipModalOpen={searchParams.get('reward') === 'open'}
          onTipModalClose={() => {
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete('reward')
            setSearchParams(newSearchParams)
          }}
        />
        {messagesLoading ? (
          <div className='flex-1 flex justify-center items-center'>
            <Loader />
          </div>
        ) : (
          <>
            <div className='flex flex-1 pb-48 lg:pb-64 flex-col-reverse overflow-y-auto no-scrollbar no-scrollbar::-webkit-scrollbar py-4'>
              <div className='px-4 sm:px-6 '>
                <ul className='space-y-4 mb-16 lg:mb-0'>
                  <CollaborationTips audience='brand' />
                  {messages?.getMessagesByThread.items.map((message: Message) =>
                    renderMessage(message),
                  )}
                  {messages?.getMessagesByThread.items.length === 0 && (
                    <p className='text-center text-gray-500'>
                      No messages yet. Start the conversation!
                    </p>
                  )}
                </ul>
              </div>

              <div ref={messagesEndRef} />
            </div>
            <ChatInput
              profilePicUrl={activeThread?.brand?.logo?.url}
              creatorId={activeThread?.creator?.id}
              brandId={activeThread?.brand?.id}
              type={MessageType.brandToCreator}
              threadId={activeThread?.id}
              templates={getBrandTemplatesData?.getBrandTemplates?.items}
            />
          </>
        )}
      </div>
    )
  }

  return (
    <div className='w-full h-full overflow-hidden'>
      <div className='flex h-full flex-1 justify-center lg:justify-start flex-row'>
        <div
          className={classNames(
            'h-full md:border-r',
            uuid ? 'hidden lg:block' : '',
          )}
        >
          <ChatMembersList
            pagination={threads?.getMessagesThreads?.pagination}
            threads={threads?.getMessagesThreads?.items?.map((thread) => ({
              id: thread.id,
              uuid: thread.uuid,
              status: thread.status,
              from: {
                name: `${thread.creator.firstName}`,
                avatar: thread.creator.avatar,
              },
              lastMessage: thread.lastMessage,
              threadUnreadMessagesCount: thread.brandUnreadMessagesCount,
              lastMessageDate: thread.lastMessageSentAt,
              type: 'user',
              onClick: () =>
                navigate({
                  pathname: BrandRoutes.inboxChat
                    .replace(':id', `${thread.brand.id}`)
                    .replace(
                      ':uuid',
                      `${thread.brand.id}-${thread.creator.id}`,
                    ),
                  search: `?${createSearchParams(searchParams)}`,
                }),
            }))}
            activeThreadId={uuid}
            onSearch={onSearch}
            onMarkAllRead={onMarkAllRead}
            fetchMore={fetchMoreThreads}
            currentBrand={currentBrand}
            showArchived={showArchived}
            onToggleArchived={onToggleArchived}
          />
        </div>

        {activeThread && renderChat()}
        {!activeThread && (
          <div className='hidden md:block flex-col w-full justify-between'>
            {!threadsLoading &&
              threads?.getMessagesThreads?.items?.length > 0 && (
                <div className='flex h-screen'>
                  <div className='m-auto'>
                    <p className='text-center'>
                      Select a creator chat to see messages
                    </p>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  )
}

export { Inbox }
